import React from "react";
import { useFormikContext } from "formik";
import { AnyObject } from "../../global";
import { useCopies } from "shared/hooks/useCopies";
import { FormFieldOption, ReadOnlyValueConfig } from "./fields/types";
import { FormValueType } from "./renderer/types/SchemaField";
import { ConflictSuffix, ConflictTagType, ConflictType } from "./renderer/enums/Conflict";
import xor from "lodash/xor";
import FieldRenderType from "./renderer/enums/FieldRenderType";
import OpenLinkInNewTab from "./core/link";
import {
  DEFAULT_DISPLAY_VALUE_CHARACTER_FOR_PASSWORD,
  DEFAULT_LENGTH_FOR_DISPLAY_VALUE_FOR_PASSWORD,
  DEFAULT_VALUE_FOR_EMPTY_FIELD
} from "shared/utils/constants";
import { PreviewImage } from "./core/assetUpload/defaultAssetPreview";
import repeat from "lodash/repeat";
import classNames from "classnames";
import FieldOptionViewType from "./renderer/enums/FieldOptionViewType";
import { TextAlignment } from "shared/enums/TextAlignment";
import DateTimeHelper from "shared/utils/dateTimeHelper";

/**
 * @name MessageIfErrorInForm
 * @description include this component in form to show form level validation message on submit.
 * @constructor
 */
export function MessageIfErrorInForm() {
  const copies = useCopies();
  const { submitCount, errors } = useFormikContext();
  const [shouldReturnErrorMsg, setShouldReturnErrorMsg] = React.useState(false);

  React.useEffect(() => {
    if (submitCount && errors && Object.keys(errors).length) {
      setShouldReturnErrorMsg(true);
    } else {
      setShouldReturnErrorMsg(false);
    }
  }, [submitCount, errors]);

  return (
    <React.Fragment>
      {shouldReturnErrorMsg && (
        <div className="form_error text-center completeAll">
          <span>{copies.get("message.if.error.in.form")}</span>
        </div>
      )}
    </React.Fragment>
  );
}

/**
 * @name ScrollToErrorOnSubmit
 * @description Use this component in forms to auto scroll to field which has error on submit
 * @constructor
 */
export function ScrollToErrorOnSubmit() {
  const { submitCount } = useFormikContext();
  React.useEffect(() => {
    if (submitCount) {
      try {
        const element = document.getElementsByClassName("form_error")[0];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch (e) {}
    }
  }, [submitCount]);
  return null;
}

// /**
//  * @name
//  * @description Use this component in field component to reset field value on unmount
//  * @constructor
//  */
// export function ResetFieldValueOnUnmount(props: { name: string; initialValue: any }) {
//   const { setFieldValue, setFieldTouched } = useFormikContext();
//   React.useEffect(() => {
//     return () => {
//       setFieldValue(props.name, props.initialValue, false);
//       setFieldTouched(props.name, false);
//     };
//   }, [setFieldValue, props.name, props.initialValue, setFieldTouched]);
//   return null;
// }

// Reset form field when it is removed from then view
export function FieldResetWhenRemoved({
  name,
  defaultInitialValue,
  fixedInitialValueOnReset
}: {
  name: string;
  defaultInitialValue?: any;
  fixedInitialValueOnReset?: any;
}) {
  const { initialValues, setFieldValue, setFieldTouched, setFieldError } = useFormikContext<AnyObject>();

  const fieldInitialValue = React.useMemo(() => initialValues[name], [initialValues, name]);

  React.useEffect(() => {
    return () => {
      if (fixedInitialValueOnReset !== undefined) {
        setFieldValue(name, fixedInitialValueOnReset);
        setFieldTouched(name, false);
        setFieldError(name, "");
        return;
      }
      setFieldValue(name, fieldInitialValue ?? defaultInitialValue ?? "");
      setFieldTouched(name, false);
      setFieldError(name, "");
    };
  }, [
    name,
    fieldInitialValue,
    setFieldValue,
    setFieldTouched,
    defaultInitialValue,
    fixedInitialValueOnReset,
    setFieldError
  ]);
  return null;
}

export function getConflictTypeForOption({
  option,
  initialUserSelectedValues,
  conflictValues
}: {
  option: FormFieldOption;
  initialUserSelectedValues?: FormValueType;
  conflictValues?: Array<string>;
}) {
  if (!initialUserSelectedValues || !conflictValues || !Array.isArray(initialUserSelectedValues)) {
    return "";
  }

  if (!initialUserSelectedValues.includes(option.value) && conflictValues.includes(option.value)) {
    return ConflictTagType.DELETED_VALUE;
  }

  if (initialUserSelectedValues.includes(option.value) && !conflictValues.includes(option.value)) {
    // if new value selected by user then show that differently
    return ConflictTagType.NEW_VALUE;
  }

  return "";
}

export function hasVisibleConflictValue({
  valuesObj,
  fieldName,
  conflictType = ConflictType.TEXT
}: {
  valuesObj: AnyObject;
  fieldName: string;
  conflictType: ConflictType;
}) {
  const isConflictValuePresent =
    valuesObj &&
    valuesObj[`${fieldName}${ConflictSuffix.VALUE}`] !== null &&
    valuesObj[`${fieldName}${ConflictSuffix.VALUE}`] !== undefined &&
    valuesObj[`${fieldName}${ConflictSuffix.VALUE}`] !== "";

  if (isConflictValuePresent) {
    switch (conflictType) {
      case ConflictType.IMAGE:
        return true;
      case ConflictType.DATE:
        return (
          DateTimeHelper.parseDate(valuesObj[fieldName]).toString() !==
          DateTimeHelper.parseDate(valuesObj[`${fieldName}${ConflictSuffix.VALUE}`]).toString()
        );
      case ConflictType.TEXT:
        return (
          valuesObj[fieldName].toString().trim() !== valuesObj[`${fieldName}${ConflictSuffix.VALUE}`].toString().trim()
        );
      case ConflictType.TAG: // todo:rr xor with lowercase of both values and conflict values
      case ConflictType.MULTISELECT:
      case ConflictType.NESTED_MULTISELECT:
        return xor(valuesObj[fieldName], valuesObj[`${fieldName}${ConflictSuffix.VALUE}`]).length !== 0;
    }
  }
  return isConflictValuePresent;
}

export function getValueForReadOnlyFieldForStaticForm(args: ReadOnlyValueConfig) {
  if (args.value === null || args.value === undefined || args.value === "") {
    return DEFAULT_VALUE_FOR_EMPTY_FIELD;
  }

  switch (args.renderType) {
    case FieldRenderType.SINGLE_LINE_TEXTFIELD:
    case FieldRenderType.MULTI_LINE_TEXTFIELD:
    case FieldRenderType.SINGLE_SELECT:
    case FieldRenderType.RADIO:
    case FieldRenderType.PHONE:
    case FieldRenderType.CHECK_BOX:
      return args.value;
    case FieldRenderType.MULTI_LINE_TEXTFIELD_HTML_INPUT:
      return <span dangerouslySetInnerHTML={{ __html: args.value as string }} />;
    case FieldRenderType.URL:
      return <OpenLinkInNewTab title={args.value as string} url={args.value as string} />;
    case FieldRenderType.DATE:
      return DateTimeHelper.formatDate({ startDate: args.value as string });
    case FieldRenderType.TAGS:
      if (!Array.isArray(args.value) || !args.value.length) {
        return DEFAULT_VALUE_FOR_EMPTY_FIELD;
      }
      return (
        <div className="fieldTags">
          {args.value.map((o: string) => (
            <span className="fieldTags--item">{o}</span>
          ))}
        </div>
      );
    case FieldRenderType.COLOR:
      return (
        <div className="fieldColor">
          <span className="fieldColor--item" style={{ backgroundColor: args.value as string }} />
          {args.value}
        </div>
      );
    case FieldRenderType.IMAGE:
      return <PreviewImage url={args.value as string} align={TextAlignment.LEFT} />;
    case FieldRenderType.PASSWORD:
      return repeat(DEFAULT_DISPLAY_VALUE_CHARACTER_FOR_PASSWORD, DEFAULT_LENGTH_FOR_DISPLAY_VALUE_FOR_PASSWORD);
    default:
      return <span>{args.value}</span>;
  }
}

export function NestedMultiSelectReadOnly(props: {
  options: Array<FormFieldOption>;
  fieldOptionViewType?: FieldOptionViewType;
}) {
  return (
    <div
      className={classNames("checkboxGroup_wrapper checkboxGroup_conflict", {
        hasSingleOption: props.fieldOptionViewType === FieldOptionViewType.SINGLE_COLUMN
      })}>
      {props.options.map(o => (
        <NestedMultiSelectParentAndChildReadOnly option={o} key={o.value} />
      ))}
    </div>
  );
}

export function NestedMultiSelectParentAndChildReadOnly(props: { option: FormFieldOption }) {
  return (
    <div className="checkboxGroup_accordion">
      <div className="checkboxGroup_accordion_title">
        {/* <div className="checkboxGroup_accordion_title_header">
          <h5>{props.option.label}</h5>
        </div> */}
        <div className="checkboxGroup_accordion_title_tags">
          <h5>{props.option.label}:</h5>
          {props.option.childOptions?.map(o => (
            <span
              key={o.value}
              title={typeof o.label === "string" ? o.label : undefined}
              className={o.customData?.className}>
              {o.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function getOptionsWithConflictTagType(args: {
  options: Array<FormFieldOption>;
  conflictValue?: FormValueType;
  initialValue?: FormValueType;
}) {
  if (Array.isArray(args.conflictValue) && args.conflictValue.length) {
    return args.options.map((opt: FormFieldOption) => {
      const conflictTagTypeForOption = getConflictTypeForOption({
        option: opt,
        initialUserSelectedValues: args.initialValue,
        conflictValues: (args.conflictValue || []) as unknown as Array<string>
      });
      const customData = opt.customData
        ? { ...opt.customData, conflictTagType: conflictTagTypeForOption }
        : { conflictTagType: conflictTagTypeForOption };
      return { ...opt, customData };
    });
  }
  return args.options;
}

export function getNestedOptionsWithConflictTagType(args: {
  options: Array<FormFieldOption>;
  conflictValue?: FormValueType;
  initialValue?: FormValueType;
}) {
  if (Array.isArray(args.conflictValue) && args.conflictValue.length) {
    return args.options.map((parentOption: FormFieldOption) => ({
      ...parentOption,
      childOptions: getOptionsWithConflictTagType({
        options: parentOption.childOptions as Array<FormFieldOption>,
        conflictValue: args.conflictValue,
        initialValue: args.initialValue
      })
    }));
  }
  return args.options;
}

/**
 * @description Use this component in set dirty flag in state which can be accessed from parent component.
 */
export function FromDirtyCheck(props: { onDirtyStateChange: (dirty: boolean) => void }) {
  const { dirty } = useFormikContext();

  React.useEffect(() => {
    props.onDirtyStateChange(dirty);
  }, [dirty, props]);

  return null;
}
